
import elementsIds from '@/const/elementIds.json';
import QTooltip from 'quasar/src/components/tooltip/QTooltip.js';;
import { mapGetters } from 'vuex';
import IncrediInputNumWithMenu from '@/components/inputs/incrediInputNumWithMenu.vue';
import TooltipBtn from '@/components/common/TooltipBtn.vue';
import IncrediCheckBox from '@/components/inputs/incrediCheckBox.vue';
import { dialogMixins } from '@/components/common/dialogs/dialogMixins';
import { LICENSE_TYPES } from '@/const/licensePage';
import AgentCertificateValidation from '@/views/settings/components/AgentCertificateValidation.vue';
import { defineComponent, PropType, inject } from 'vue';
import { ValidPortsType } from '@/views/settings/settings.types';
import objectPath from 'object-path';
import { IKSettingsDirty, IKSettingsValidPorts } from '@/types/injectionKeys';
import { QUERY_RESULT_PATH, useLicensePageQuery, SettingsPageWindowsQuery, useCommonDataQuery } from '@/graphql/codegen/generated/graphql';
import incrediLink from '@/components/common/incrediLink.vue';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { IncrediTooltip } from 'incredi-ui';
import { useSettingsErrorMessages } from '@/views/settings/settings.helpers';
import { showAllowCustomDistributionProfiles } from '@/globals/functions/licenseValidationRules';
import SettingsActiveDirectory from '@/views/settings/components/settingsActiveDirectory.vue';
import { FeatureFlags } from '@/types/utils';
import { Constants } from '@/const/globals';
import { SETTINGS_OBJ } from '../../const';
import SettingsPortInput from '../../components/settingsPortInput.vue';
import util from '../../util';
import SettingsItemBox from '../../components/settings-item-box.vue';
import SettingsBackupDropdownBox from '../../components/settings-backup-dropdown-box.vue';

export default defineComponent({
  name: 'SettingsGeneral',
  components: {
    SettingsActiveDirectory,
    QTooltip,
    IncrediCheckBox,
    IncrediInputNumWithMenu,
    TooltipBtn,
    SettingsPortInput,
    SettingsItemBox,
    SettingsBackupDropdownBox,
    AgentCertificateValidation,
    incrediLink,
    IncrediTooltip,
  },

  mixins: [dialogMixins],
  props: {
    settingsObject: {
      type: Object as PropType<NonNullable<SettingsPageWindowsQuery['ibManagerEntities']>>,
      required: true,
    },
    handleInputWithMenuSelection: {
      required: true,
      type: Function as PropType<() => void>,
    },
    hasApiAccessFeature: {
      required: true,
      type: Boolean,
    },
  },
  emits: ['input', 'change'],
  setup() {
    const coordinatorsService = inject(IKCoordinatorsService);
    const userRolePermissions = useUserRolePermissions();

    const settingsDirty = inject(IKSettingsDirty);
    const GQL_userLicenseInfoQuery = useLicensePageQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    const validPorts = inject(IKSettingsValidPorts);
    const { getPortRangeValidationErrorMessage } = useSettingsErrorMessages();
    const GQL_CommonData = useCommonDataQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));

    return {
      settingsDirty,
      QUERY_RESULT_PATH,
      objectPath,
      validPorts,
      GQL_userLicenseInfoQuery,
      userRolePermissions,
      getPortRangeValidationErrorMessage,
      elementsIds,
      GQL_CommonData,
    };
  },
  data() {
    return {
      SETTINGS_OBJ,
      LICENSE_TYPES,
      // supportMailUrl: process.env.VUE_APP_SUPPORT_MAIL,
      prometheusTooltip: false,
    };
  },
  computed: {
    ...mapGetters({
      inputsWithMenuSelectedOption: ['settings/getInputsWithMenuSelectedOption'] as any,
    }),
    util() {
      return util;
    },
    license() {
      return this.GQL_userLicenseInfoQuery?.result?.value?.ibManagerEntities?.license;
    },
    featureFlags() {
      return this.GQL_CommonData?.result?.value?.ibManagerEntities?.featureFlags ? (JSON.parse(this.GQL_CommonData?.result?.value?.ibManagerEntities?.featureFlags) as FeatureFlags) : null;
    },
    shouldDisableTelemetry() {
      return this.license?.windows?.type === LICENSE_TYPES.Free || this.license?.windows?.type === LICENSE_TYPES.Trial;
    },
    showAllowCustomDistributionProfiles() {
      // Visible if the license has "Visual Studio C++" AND "Make and build tools"
      // and NOT "generic acceleration"
      return showAllowCustomDistributionProfiles(this.license?.windows?.features);
    },
    showExternalManagement() {
      return (
        this.license?.windows?.features?.productFeatures?.includes(Constants.LICENSE_FEATURES.EXTERNAL_USER_MANAGEMENT) ||
        this.license?.windows?.tierName?.toLowerCase().includes(Constants.TIERS.ENTERPRISE.toLowerCase())
      );
    },
  },

  methods: {
    areMorePortsAreDirty(portField: keyof ValidPortsType) {
      if (this.validPorts && this.settingsDirty) {
        const ports = Object.keys(this.validPorts).filter(key => key !== portField);
        return ports.some(key => Object.prototype.hasOwnProperty.call(this.settingsDirty?.inputsDirty.value, key));
      }
    },
    toggleTooltip(val: boolean) {
      if (!this.hasApiAccessFeature) {
        this.prometheusTooltip = val;
      }
    },
  },
});
